import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormField,
  FormMessage,
} from '@predoc/cow';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

import {
  editNameSchema,
  EditNameFormData,
  EditNameFormFieldProps,
  UserProfile,
} from '../types';
import {
  useUpdateName,
  useInvalidateUserProfile,
} from '../hooks/userSettingApi';

interface EditNameModalProps {
  currentUser: UserProfile;
}

export const EditNameModal = ({ currentUser }: EditNameModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const updateNameMutation = useUpdateName();
  const invalidateUserProfile = useInvalidateUserProfile();

  const form = useForm<EditNameFormData>({
    resolver: zodResolver(editNameSchema),
    defaultValues: {
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
    },
    mode: 'onChange',
  });

  const handleOpen = () => {
    form.reset({
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
    });
    setIsOpen(true);
  };

  const handleClose = () => {
    form.reset();
    setIsOpen(false);
  };

  const handleSave = async (data: EditNameFormData) => {
    try {
      await updateNameMutation.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
      });

      toast.success('Name updated successfully');
      invalidateUserProfile();
      handleClose();
    } catch {
      toast.error('Failed to update name. Please try again.');
    }
  };

  const hasValidationErrors = Object.keys(form.formState.errors).length > 0;

  return (
    <>
      <button
        onClick={handleOpen}
        className="ml-2 text-gray-500 hover:text-gray-700"
        aria-label="Edit name"
      >
        <Pencil className="h-4 w-4" />
      </button>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Name</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSave)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }: EditNameFormFieldProps<'firstName'>) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter your first name"
                        aria-label="First Name"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }: EditNameFormFieldProps<'lastName'>) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter your last name"
                        aria-label="Last Name"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage className="text-destructive" />
                  </FormItem>
                )}
              />

              <DialogFooter className="items-center border-t pt-4">
                {hasValidationErrors && form.formState.isSubmitted && (
                  <FormMessage className="text-destructive">
                    Please fix validation errors
                  </FormMessage>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={updateNameMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !form.formState.isValid || updateNameMutation.isPending
                  }
                  loading={updateNameMutation.isPending}
                >
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
